import React, { useEffect, useState, memo } from 'react';
import set from 'lodash/set';
import get from 'lodash/get';
import styles from './FeedbackForm.module.scss';
import {
  FeedbackForm as FeedbackFormType,
  FeedbackFormElement, FeedbackFormElementMultiScale,
  FeedbackFormElementSelect,
  FeedbackFormElementText,
  FeedbackFormElementTypeEnum,
} from '../../../types';
import { TextInput, TextInputValue } from '../../../components/text-input/TextInput';
import RadioGroup from '../../../components/radio-group/RadioGroup';
import CheckboxGroup from '../../../components/checkbox-group/CheckboxGroup';
import Button from '../../../components/button/Button';
import { feedbackFormHasAnswerErrors, feedbackSingleFormHasAnswerError, GENDER_MAX_ANSWER_LIMIT } from '../../../utils/validation';
import {
  RequiredAnswerAnnouncer,
  enterErrorMessage,
  lengthErrorMessage,
  multiScaleErrorMessage,
  selectErrorMessage,
  useCurrentQuestionUnanswered,
} from './RequiredAnswer';
import MultiScaleComponent from './multi-scale/MultiScaleElement';
import { useMediaQuery } from 'react-responsive';
import { TextInputWithLengthLimit } from '@src/components/text-input/TextInputWithLengthLimit';
import TextSpeech from '@src/components/text-speech/TextSpeech';

type Props = {
  form: FeedbackFormType;
  onSubmit: (form: string) => void;
  loading: boolean;
  isAU: boolean;
};

function FeedbackForm(props: Props) {
  const { form, onSubmit, loading, isAU=false } = props;

  const [elements, setElements] = useState<FeedbackFormElement[]>([]);
  const [showError, setShowError] = useState(false);
  const [currentIndex, setIndex] = useState(-1);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    if (form?.elements) {
      setElements(form.elements);
    }
  }, [form]);

  useEffect(() => {
    if (!form.contentInstruction && !form.instructions) {
      setIndex(0)
    }
  }, [form])

  const handleChangeExplanation = (index: number) => (value: TextInputValue) => {
    set(elements, `${index}.answer.answerExplanation`, value);
    setElements([...elements]);
    if (showError) setShowError(false);
  };

  const handleChangeText = (index: number) => (value: TextInputValue) => {
    set(elements, `${index}.answer.value`, value);
    setElements([...elements]);
    if (showError) setShowError(false);
  };

  const handChangeRadio = (index: number) => (value: string) => {
    set(elements, `${index}.answer.value`, value);

    setElements([...elements]);
    if (showError) setShowError(false);
  };

  const handleChangeCheckbox = (index: number) => (value: string[]) => {
    set(elements, `${index}.answer.value`, value);

    setElements([...elements]);
    if (showError) setShowError(false);
  };

  const handleChangeMultiScaleAnswer = (index: number) => (id: string, value: string) => {
    set(elements, `${index}.answer.${id}`, value);

    setElements([...elements]);
    if (showError) setShowError(false);
  };

  const handleSubmit = () => {
    const hasError = (currentIndex < 0 ? false : feedbackSingleFormHasAnswerError(elements[currentIndex])) || feedbackFormHasAnswerErrors(elements);
    if (hasError) {
      setShowError(true);
    } else {
      const data = {
        ...form,
        elements
      };

      onSubmit(JSON.stringify(data));
    }
  };

  const renderQAItem = (element: FeedbackFormElement, index: number) => {
    let content;
    switch (element.type) {
      case FeedbackFormElementTypeEnum.text: {
        const e = element as FeedbackFormElementText;
        const hasError = showError && !e.answer?.value;
        const hasLengthError = showError && e.lengthLimit && e.answer?.value?.length! > GENDER_MAX_ANSWER_LIMIT
        content = e.lengthLimit ? (
          <>
            <div className={styles.textLabel}>{e.value}
              <TextSpeech text={e.value} isAU={isAU} />
            </div>
            <TextInputWithLengthLimit
              label=""
              id={e.id}
              maxLength={20}
              value={e.answer?.value ?? ''}
              onChange={handleChangeText(index)}
              hasError={hasError || hasLengthError}
              subTitle={hasError ? enterErrorMessage : (hasLengthError ? lengthErrorMessage : '')}
              showValidationMessage
            />
          </>
        ) : (
          <>
            <div className={styles.textLabel}>{e.value}
              <TextSpeech text={e.value} isAU={isAU} />
            </div>
            <TextInput
              label=""
              // placeholder={e.value}
              id={e.id}
              value={e.answer?.value ?? ''}
              onChange={handleChangeText(index)}
              hasError={hasError}
              subTitle={hasError ? enterErrorMessage : ''}
              showValidationMessage
            />
          </>
        );
        break;
      }
      case FeedbackFormElementTypeEnum.textarea: {
        const e = element as FeedbackFormElementText;
        const hasError = showError && !e.answer?.value;
        content = (
          <>
            <div className={styles.textLabel}>{e.value}
              <TextSpeech text={e.value} isAU={isAU} />
            </div>
            <TextInput
              textArea={true}
              label={""}
              // placeholder={e.value}
              id={e.id}
              value={e.answer?.value ?? ''}
              onChange={handleChangeText(index)}
              hasError={hasError}
              subTitle={hasError ? enterErrorMessage : ''}
              showValidationMessage
              allowSpecialCharacters={false}
            />
          </>
        );
        break
      }
      case FeedbackFormElementTypeEnum.radio:
      case FeedbackFormElementTypeEnum.radioAssessable: {
        const e = element as FeedbackFormElementSelect;
        const options = e.options ? e.options.map(o => ({ label: o, value: o })) : [];
        // A radio answer is always a single value; lodash widens the default to string | string[].
        const value = get(elements, `${index}.answer.value`, '') as string;
        const hasError = showError && !value;
        content = (
          <div>
            <div className={styles.label}>{element.value}
            <TextSpeech text={e.value} isAU={isAU} />
            </div>
            <RadioGroup
              value={value}
              options={options}
              onChange={handChangeRadio(index)}
              name={e.id}
              hasError={hasError}
              subTitle={hasError ? selectErrorMessage : ''}
              touchable
              needSpeak
              isAU={isAU}
            />
          </div>
        );
        break;
      }
      case FeedbackFormElementTypeEnum.checkbox:
      case FeedbackFormElementTypeEnum.checkboxAssessable: {
        const e = element as FeedbackFormElementSelect;
        const options = e.options ? e.options.map(o => ({ label: o, value: o })) : [];
        // A checkbox answer is always a list; lodash widens the default to string | string[].
        const value = get(elements, `${index}.answer.value`, []) as string[];
        const hasError = showError && value.length === 0;
        content = (
          <div>
            <div className={styles.label}>{element.value}
            <TextSpeech text={e.value} isAU={isAU} />
            </div>
            <CheckboxGroup
              value={value}
              options={options}
              onChange={handleChangeCheckbox(index)}
              hasError={hasError}
              subTitle={hasError ? selectErrorMessage : ''}
              touchable
              needSpeak
              isAU={isAU}
            />
          </div>
        );
        break;
      }
      case FeedbackFormElementTypeEnum.multiScale:
      case FeedbackFormElementTypeEnum.multiScaleAssessable: {
        const e = element as FeedbackFormElementMultiScale;
        const options = e.options ?? [];
        const statements = e.statements ?? [];
        const value = e.answer ?? {};
        const hasError = showError && (!value || Object.keys(value).length < statements.length);
        content = (
          <div>
            <div className={styles.label}>{element.value}
            <TextSpeech text={e.value} isAU={isAU} />
            </div>
            <MultiScaleComponent
              value={value}
              options={options}
              statements={statements}
              hasError={hasError}
              errorMessage={hasError ? multiScaleErrorMessage : ''}
              onChange={handleChangeMultiScaleAnswer(index)}
              needSpeak
              isAU={isAU}
            />
          </div>
        );
        break;
      }
      default: {
        content = null;
      }
    }
    const firstRow = index === 0;
    return (
      <div className={`${styles.row} ${firstRow ? styles.firstRow : styles.QARowItem}`} key={element.id}>
        {content}
        {element.askExplanation && (
          <div>
          <label className={styles.label} htmlFor={`${element.id}_askExplanation`}>Please explain your Answer
            <TextSpeech text={'Please explain your Answer'} isAU={isAU} />
          </label>
          <TextInput
            label=""
            placeholder="Please explain your Answer"
            id={`${element.id}_askExplanation`}
            value={element.answer?.answerExplanation ?? ''}
            onChange={handleChangeExplanation(index)}
            hasError={showError && (!element.answer?.answerExplanation || element.answer?.answerExplanation === '')}
            subTitle={showError && (!element.answer?.answerExplanation || element.answer?.answerExplanation === '') ? enterErrorMessage : ''}
            className={styles.answerExplanation}
            small
            showValidationMessage
          />
          </div>
        )}
      </div>
    );
  }

  const goToNextStep = () => {
    if (!elements[currentIndex + 1]) return
    const hasError = currentIndex < 0 ? false : feedbackSingleFormHasAnswerError(elements[currentIndex]);

    if (hasError) {
      setShowError(true);
    } else {
      setIndex((index) => index + 1)
    }
  }

  const currentUnanswered = useCurrentQuestionUnanswered(elements, currentIndex);

  const goToPreviousStep = () => {
    setShowError(false);
    setIndex((index) => index - 1)
  }

  return (
    <>
      {currentIndex === -1 && (
        <>
          {!!form.contentInstruction ? 
          <div className={styles.description}>{form.contentInstruction}
            <TextSpeech text={form.contentInstruction} isAU={isAU} />
          </div>
          : (!!form.instructions && 
          <div className={styles.description}>{(form.instructions || '').replace('\'n', '')}
            <TextSpeech text={form.instructions} isAU={isAU} />
          </div>)}
        </>
      )}
      {elements[currentIndex] && renderQAItem(elements[currentIndex], currentIndex)}
      <RequiredAnswerAnnouncer element={elements[currentIndex]} show={showError && currentUnanswered} />
      <div className={styles.oneRow}>
        { currentIndex >= 0 ?
         (currentIndex === 0 && !form.contentInstruction && !form.instructions) ? null : 
          <Button disabled={loading} className={styles.simpleButton} label={'Previous'} onClick={goToPreviousStep} size={isMobile ? 'tiny' : 'small'}/> : <div></div>}
        { currentIndex === elements.length - 1 ? (
            <Button disabled={loading} className={`${styles.simpleButton} ${currentUnanswered ? styles.fadedButton : ''}`} label={'Submit'} onClick={handleSubmit} size={isMobile ? 'tiny' : 'small'}/>
          ) : (
            <Button disabled={loading} className={`${styles.simpleButton} ${currentUnanswered ? styles.fadedButton : ''}`} label={' Next '} onClick={goToNextStep} size={isMobile ? 'tiny' : 'small'}/>
          )
        }
      </div>
    </>
  );
}

export default memo(FeedbackForm);
